import { ReactNode, useEffect } from "react";
import {
  Link,
  // NavLink
} from "react-router-dom";
import { Logo } from "./Logo";
import { useOrganizationDetails } from "../../hooks/use-organization-details";
import s from "./MainLayout.module.scss";

interface Props {
  children: ReactNode;
  contentClassName?: string;
}

export const MainLayout = ({ children, contentClassName }: Props) => {
  const organizationDetails = useOrganizationDetails();
  useEffect(() => {
    document.title =
      organizationDetails?.websiteTitle ||
      organizationDetails?.title ||
      "Rezervacijų sistema - TakeBlock";

    const styleElement = document.createElement("style");
    styleElement.appendChild(
      document.createTextNode(`
        :root {
          --brand-color: ${organizationDetails?.brandColor?.main || "#5e5e5e"};
          --brand-color-alpha: ${
            organizationDetails?.brandColor?.alpha || "#5e5e5e80"
          };
          --brand-color-darken: ${
            organizationDetails?.brandColor?.darken || "#4a4a4a"
          };
        }
      `)
    );
    document.head.appendChild(styleElement);

    document
      .getElementById("icon96")
      ?.setAttribute(
        "href",
        organizationDetails?.favicon?.icon96 || "/favicon-96x96.png"
      );

    document
      .getElementById("iconSvg")
      ?.setAttribute(
        "href",
        organizationDetails?.favicon?.iconSvg || "/favicon.svg"
      );

    document
      .getElementById("iconShortcut")
      ?.setAttribute(
        "href",
        organizationDetails?.favicon?.iconShortcut || "/favicon.ico"
      );

    document
      .getElementById("iconAppleTouch")
      ?.setAttribute(
        "href",
        organizationDetails?.favicon?.iconAppleTouch || "/apple-touch-icon.png"
      );

    document
      .getElementById("appleMobileWebAppTitle")
      ?.setAttribute(
        "content",
        organizationDetails?.favicon?.appleMobileWebAppTitle || "TakeBlock"
      );

    document
      .getElementById("siteManifest")
      ?.setAttribute(
        "href",
        organizationDetails?.favicon?.siteManifest || "/site.webmanifest"
      );

    return () => {
      styleElement.remove();
    };
  }, [
    organizationDetails?.brandColor,
    organizationDetails?.favicon?.appleMobileWebAppTitle,
    organizationDetails?.favicon?.icon96,
    organizationDetails?.favicon?.iconAppleTouch,
    organizationDetails?.favicon?.iconShortcut,
    organizationDetails?.favicon?.iconSvg,
    organizationDetails?.favicon?.siteManifest,
    organizationDetails?.title,
    organizationDetails?.websiteTitle,
  ]);

  if (!organizationDetails) {
    return null;
  }

  const { title, phoneNumber, emailAddress, address, alias } =
    organizationDetails;
  const organizationHomePath = `/${alias}`;

  return (
    <div className={s.mainLayout}>
      <div className={s.layoutContainer}>
        <header className={s.mainHeader}>
          <Link to={organizationHomePath}>
            <Logo className={s.logo} />
          </Link>
          {/* Leave the navigation out, until more items are needed. */}
          {/* <nav>
                        <NavLink to={"/${organizationDetails.alias}/rezervacija"}>Rezervacija</NavLink>
                    </nav> */}
        </header>
        <main className={contentClassName}>{children}</main>
      </div>
      <div className={s.footerContainer}>
        <footer>
          <Link to={organizationHomePath}>
            <Logo className={s.logo} colorHex="#fff" />
          </Link>
          <article>
            <span className={s.footerTitle}>{title}</span>
            <address>{address}</address>
          </article>
          <article>
            <a href={`tel:${phoneNumber}`}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20px"
                height="25px"
                viewBox="0 0 19 25"
                version="1.1"
              >
                <g id="surface1">
                  <path
                    style={{ fill: "#fff" }}
                    d="M 18.992188 17.300781 C 18.992188 17.550781 18.949219 17.878906 18.855469 18.28125 C 18.765625 18.6875 18.671875 19.003906 18.574219 19.238281 C 18.386719 19.703125 17.835938 20.195312 16.929688 20.71875 C 16.082031 21.191406 15.246094 21.425781 14.417969 21.425781 C 14.175781 21.425781 13.941406 21.410156 13.707031 21.378906 C 13.472656 21.347656 13.214844 21.289062 12.929688 21.203125 C 12.648438 21.121094 12.433594 21.050781 12.292969 21.003906 C 12.152344 20.953125 11.90625 20.859375 11.546875 20.71875 C 11.191406 20.578125 10.96875 20.492188 10.886719 20.464844 C 10.003906 20.140625 9.21875 19.753906 8.527344 19.308594 C 7.382812 18.574219 6.195312 17.566406 4.964844 16.296875 C 3.734375 15.023438 2.761719 13.792969 2.050781 12.613281 C 1.617188 11.894531 1.246094 11.082031 0.929688 10.171875 C 0.902344 10.085938 0.824219 9.859375 0.6875 9.488281 C 0.554688 9.117188 0.460938 8.859375 0.410156 8.710938 C 0.359375 8.566406 0.292969 8.347656 0.214844 8.058594 C 0.136719 7.765625 0.0820312 7.5 0.046875 7.253906 C 0.015625 7.011719 0 6.765625 0 6.515625 C 0 5.660156 0.230469 4.796875 0.6875 3.921875 C 1.191406 2.980469 1.667969 2.414062 2.117188 2.21875 C 2.34375 2.117188 2.652344 2.019531 3.039062 1.925781 C 3.429688 1.832031 3.75 1.785156 3.992188 1.785156 C 4.117188 1.785156 4.214844 1.800781 4.277344 1.828125 C 4.4375 1.882812 4.675781 2.238281 4.992188 2.886719 C 5.089844 3.0625 5.222656 3.316406 5.394531 3.640625 C 5.566406 3.964844 5.722656 4.261719 5.867188 4.527344 C 6.011719 4.792969 6.148438 5.042969 6.285156 5.273438 C 6.3125 5.3125 6.390625 5.425781 6.523438 5.621094 C 6.652344 5.816406 6.75 5.984375 6.8125 6.117188 C 6.875 6.253906 6.90625 6.386719 6.90625 6.515625 C 6.90625 6.703125 6.777344 6.933594 6.523438 7.210938 C 6.265625 7.492188 5.988281 7.746094 5.683594 7.980469 C 5.382812 8.214844 5.105469 8.460938 4.847656 8.71875 C 4.59375 8.980469 4.464844 9.191406 4.464844 9.359375 C 4.464844 9.445312 4.488281 9.550781 4.53125 9.675781 C 4.578125 9.800781 4.617188 9.894531 4.644531 9.960938 C 4.675781 10.023438 4.742188 10.136719 4.835938 10.296875 C 4.933594 10.453125 4.984375 10.542969 4.992188 10.558594 C 5.675781 11.835938 6.457031 12.929688 7.339844 13.839844 C 8.21875 14.75 9.277344 15.5625 10.507812 16.265625 C 10.527344 16.277344 10.609375 16.328125 10.765625 16.425781 C 10.917969 16.527344 11.023438 16.589844 11.089844 16.621094 C 11.152344 16.652344 11.242188 16.691406 11.363281 16.738281 C 11.484375 16.785156 11.585938 16.808594 11.667969 16.808594 C 11.828125 16.808594 12.035156 16.675781 12.289062 16.414062 C 12.542969 16.148438 12.78125 15.859375 13.003906 15.546875 C 13.226562 15.234375 13.472656 14.949219 13.746094 14.683594 C 14.015625 14.417969 14.242188 14.285156 14.417969 14.285156 C 14.546875 14.285156 14.675781 14.316406 14.804688 14.382812 C 14.933594 14.449219 15.09375 14.546875 15.285156 14.683594 C 15.476562 14.816406 15.589844 14.898438 15.621094 14.925781 C 15.847656 15.066406 16.085938 15.210938 16.34375 15.359375 C 16.601562 15.507812 16.886719 15.671875 17.199219 15.847656 C 17.511719 16.023438 17.757812 16.164062 17.925781 16.265625 C 18.558594 16.59375 18.898438 16.839844 18.953125 17.003906 C 18.980469 17.070312 18.992188 17.171875 18.992188 17.300781 Z M 18.992188 17.300781"
                  />
                </g>
              </svg>
              {phoneNumber}
            </a>
            <a href={`mailto:${emailAddress}`}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25px"
                height="25px"
                viewBox="0 0 25 25"
                version="1.1"
              >
                <g>
                  <path
                    style={{ fill: "#fff" }}
                    d="M 23.214844 20.984375 L 23.214844 10.265625 C 22.917969 10.601562 22.597656 10.910156 22.253906 11.1875 C 19.757812 13.105469 17.777344 14.675781 16.308594 15.90625 C 15.835938 16.304688 15.449219 16.617188 15.148438 16.835938 C 14.851562 17.058594 14.449219 17.285156 13.945312 17.515625 C 13.441406 17.746094 12.964844 17.859375 12.515625 17.859375 L 12.484375 17.859375 C 12.039062 17.859375 11.5625 17.742188 11.054688 17.515625 C 10.546875 17.285156 10.144531 17.0625 9.851562 16.835938 C 9.554688 16.613281 9.167969 16.304688 8.691406 15.90625 C 7.222656 14.675781 5.242188 13.105469 2.746094 11.1875 C 2.402344 10.910156 2.082031 10.601562 1.785156 10.265625 L 1.785156 20.984375 C 1.785156 21.101562 1.828125 21.207031 1.917969 21.296875 C 2.003906 21.382812 2.109375 21.425781 2.234375 21.425781 L 22.765625 21.425781 C 22.890625 21.425781 22.996094 21.382812 23.082031 21.296875 C 23.171875 21.207031 23.214844 21.101562 23.214844 20.984375 Z M 23.214844 6.320312 L 23.214844 5.976562 C 23.214844 5.976562 23.210938 5.917969 23.207031 5.796875 C 23.203125 5.675781 23.1875 5.621094 23.164062 5.621094 C 23.140625 5.625 23.117188 5.585938 23.085938 5.496094 C 23.058594 5.410156 23.015625 5.375 22.960938 5.390625 C 22.90625 5.410156 22.84375 5.398438 22.765625 5.359375 L 2.234375 5.359375 C 2.109375 5.359375 2.003906 5.402344 1.917969 5.488281 C 1.828125 5.578125 1.785156 5.683594 1.785156 5.800781 C 1.785156 7.367188 2.46875 8.6875 3.835938 9.765625 C 5.632812 11.179688 7.496094 12.652344 9.429688 14.1875 C 9.488281 14.234375 9.648438 14.371094 9.921875 14.601562 C 10.191406 14.828125 10.402344 15.003906 10.558594 15.121094 C 10.714844 15.242188 10.925781 15.390625 11.183594 15.5625 C 11.441406 15.734375 11.675781 15.863281 11.886719 15.945312 C 12.09375 16.027344 12.296875 16.070312 12.484375 16.074219 L 12.515625 16.074219 C 12.699219 16.074219 12.898438 16.03125 13.113281 15.945312 C 13.328125 15.859375 13.5625 15.734375 13.816406 15.5625 C 14.070312 15.390625 14.277344 15.246094 14.441406 15.121094 C 14.601562 15 14.816406 14.824219 15.078125 14.601562 C 15.34375 14.375 15.507812 14.238281 15.570312 14.1875 C 17.503906 12.652344 19.367188 11.179688 21.164062 9.765625 C 21.664062 9.363281 22.132812 8.828125 22.566406 8.15625 C 22.996094 7.480469 23.214844 6.871094 23.214844 6.320312 Z M 25 5.800781 L 25 20.984375 C 25 21.597656 24.78125 22.121094 24.34375 22.558594 C 23.910156 22.996094 23.382812 23.214844 22.765625 23.214844 L 2.234375 23.214844 C 1.621094 23.214844 1.09375 22.996094 0.65625 22.558594 C 0.214844 22.121094 0 21.59375 0 20.984375 L 0 5.800781 C 0 5.1875 0.21875 4.664062 0.65625 4.226562 C 1.089844 3.789062 1.617188 3.574219 2.234375 3.574219 L 22.765625 3.574219 C 23.378906 3.574219 23.90625 3.789062 24.34375 4.226562 C 24.785156 4.664062 25 5.1875 25 5.800781 Z M 25 5.800781 "
                  />
                </g>
              </svg>
              {emailAddress}
            </a>
          </article>
        </footer>
      </div>
    </div>
  );
};
